/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Spinner} from "react-bootstrap";
import {HorizontalTabs} from "../../../components/Tabs/HorizontalTabs";
import {useEffect, useState} from 'react';
import {useSearchParams} from "react-router-dom";
import {useAppSelector} from "../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import {Toolbar, Typography} from '@mui/material';
import {usePostRequest} from "../../../../api/helper";
import {fetchRealWorldObjectsEndpoint, signObjectsTitle} from "../../objectApp/constants";
import {ETrackedItemTypes} from "../../../../constants/trackedItems";
import SignsRWODetailsTab from "./SignsRWODetailsTab";
import {StringParam, useQueryParam} from "use-query-params";


const SignsRWODetails = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tabs, setTabs] = useState<any>([])
  const [title, setTitle] = useState("");
  const [searchParams, _] = useSearchParams();
  const urlParams = Object.fromEntries(searchParams)
  const [renderKey, setRenderKey] = useState(0);

  // Function to trigger re-render
  const triggerReRender = () => {
    setRenderKey(prevKey => prevKey + 1);
  };

  const params: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const objectId = params?.id

  useEffect(() => {
    fetchBaselineDataRWO();
  }, [renderKey]);

  const [objectName, setObjectName] = useQueryParam('objectName', StringParam);

  const fetchBaselineDataHandleRWO = usePostRequest(fetchRealWorldObjectsEndpoint)
  const fetchBaselineDataRWO = () => {
    setIsLoading(true);

    fetchBaselineDataHandleRWO()
      .then((data) => {
        const picklistmatchingObjects = data
          .filter((item: { type: string }) => item.type === "Sign-Picklist");

        // get all signMessages from Picklist object
        const signMessagesList = picklistmatchingObjects
          .map((item: any) => item['Picklist-SignMessages'])
          .filter((messages: any) => Array.isArray(messages))
          .flat()
          .map((msgObject: any) => msgObject.signMessage)
          .filter((msg: any) => typeof msg === 'string')

        // get all distinct messageType from Picklist object
        const messageTypeList = picklistmatchingObjects
          .map((item: any) => item['Sign Message Types'])
          .filter((messages: any) => Array.isArray(messages))
          .flat()
          .map((msgObject: any) => msgObject.adHocCategory)
          .filter((msg: any) => typeof msg === 'string')

        // get all distinct signShapes from Picklist object
        const signShapeList = picklistmatchingObjects
          .map((item: any) => item['Picklist-SignShapes'])
          .filter((messages: any) => Array.isArray(messages))
          .flat()
          .map((msgObject: any) => msgObject.shape)
          .filter((msg: any) => typeof msg === 'string')

        // get all distinct signColors from Picklist object
        const signColorsList = picklistmatchingObjects
          .map((item: any) => item['Picklist-Colors'])
          .filter((messages: any) => Array.isArray(messages))
          .flat()
          .map((msgObject: any) => msgObject.colorPicklist)
          .filter((msg: any) => typeof msg === 'string')

        const keyToCheck = objectName != null ? 'signName' : 'id'
        const valueToCheck = objectName != null ? objectName : objectId
        const filteredData = data.find((item: { id: string, signName: string }) => item[keyToCheck] === valueToCheck);

        if (filteredData) {
          //   const objectName = filteredData.objectName;
          const signName = filteredData.signName;
          setObjectName(signName)
          const objectType = filteredData.type;

          // Filter data again to find all instances with the same objectName
          const matchingObjects = data.filter((item: { signName: string, type: string }) => item.signName === signName && item.type === objectType);

          // Extract signVariants from the matchingObjects
          let signVariants = matchingObjects.flatMap((obj: { signVariants: any; }) => obj.signVariants);

          // Get Picklist-SignSizes and Picklist-SignUsageSpeed from signPicklist
          const signSizes = picklistmatchingObjects.flatMap((picklist: { [x: string]: any; }) => picklist["Picklist-SignSizes"]);
          const usageSpeeds = picklistmatchingObjects.flatMap((picklist: { [x: string]: any; }) => picklist["Picklist-SignUsageSpeed"]);

          // Create a map for quick lookup
          const sizeMap = new Map(signSizes.map((size: { sizeID: any; }) => [size?.sizeID, size]));
          const speedMap = new Map(usageSpeeds.map((speed: { speedID: any; }) => [speed.speedID, speed]));

          // Modify each signVariant by adding details from sizeMap and speedMap
          const signVariantsModified = signVariants.map((variant: { sizeID: unknown; usageSpeedID: unknown; }) => {
            const sizeDetails = sizeMap.get(variant?.sizeID) || {};
            const speedDetails = speedMap.get(variant?.usageSpeedID) || {};

            // Combine existing variant details with size and speed details
            return {
              ...variant,
              ...sizeDetails,
              ...speedDetails
            };
          });

          // test- adding signMessages array from Picklist (all values) as key to each matching object
          const updatedMatchingObjects = matchingObjects.map((item: any) => ({
            ...item,
            signMessagesList,
            messageTypeList,
            signShapeList,
            signColorsList,
            signVariantsModified,
            signSizes,
            usageSpeeds
          }))

          processData(updatedMatchingObjects)

          // Return the filtered objects
          return matchingObjects;
        }
      })
      .finally(() => setIsLoading(false));
  }


  const processData = (objectData: any) => {
    const localTabs: any[] = []
    const type = objectData[0].signName
    setTitle(`Sign Objects - ${type} Properties`);


    const SignsRWODetailsContent = <SignsRWODetailsTab
      objectData={objectData}
      fetchBaselineData={fetchBaselineDataRWO}
      triggerReRender={triggerReRender}
      isCustomObject={type === "Custom"}
    />

    localTabs.push({'name': 'Sign Object Details', 'content': SignsRWODetailsContent})
    setTabs(localTabs)
  }

  const triggerPMORefresh = () => {
    const event = new Event('refreshPMOTable');
    window.dispatchEvent(event);
  };

  const dialogState = useAppSelector(selectGlobalDialogState); // Call the hook outside

  useEffect(() => {
    // Check if the dialog was closed
    if (!dialogState.isOpen) {
      triggerPMORefresh(); // Trigger refresh when dialog is closed
    }
  }, [dialogState]); // Add dialogState to the dependency array

  const icons = (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <CloseGlobalDialogButton/>
    </div>
  )
  useEffect(() => {
    document.title = title;
    return () => {
      document.title = signObjectsTitle + ' - Landing';

    };
  }, [title]);
  return (
    <>
      <div style={{height: '100%', width: '100%', display: 'flex', flexFlow: 'column nowrap', overflow: 'auto'}}>
        <div>
          <Toolbar variant="dense" style={{justifyContent: "space-between", background: '#3481d5', height: "100%"}}>
            <Typography sx={{color: '#ffffff', fontSize: 14}}>{title}</Typography>
            {icons}
          </Toolbar>
        </div>
        {isLoading ?
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
            <Spinner animation={'border'} style={{color: 'grey'}}/>
          </div>
          :
          <div style={{height: '100%', overflow: 'auto', display: 'flex', justifyContent: 'space-between'}}>
            <HorizontalTabs initContent={null} initHtabIndex={0} horizontalTabs={tabs} align={'left'}
                            favoriteType={ETrackedItemTypes.OBJECT} uid={'htabs-1'}/>
          </div>
        }
      </div>
    </>
  )
}
export default SignsRWODetails

